import type { LegalDocPair } from '../types'

/**
 * Política de cookies.
 *
 * Describe lo que el código guarda de verdad en el navegador: la sesión, el
 * idioma y la apariencia, alguna preferencia del editor, y las dos cookies de
 * los sitios publicados (contraseña de un sitio o página, y modo propietario).
 * La analítica de los sitios no usa cookies. Si se añade una cookie nueva, se
 * añade aquí; si algún día se activan métricas opcionales, se pedirá permiso.
 */
export const COOKIES: LegalDocPair = {
  en: {
    title: 'Cookie Policy',
    summary: 'The few cookies and browser storage véltica uses, and what they are for.',
    intro: [
      'This Cookie Policy explains the cookies and similar technologies, such as browser local storage, that véltica uses on veltica.com, in the dashboard and the editor, and on the Sites we host. It is part of our [Privacy Policy](doc:privacy).',
    ],
    sections: [
      {
        title: 'What they are',
        blocks: [
          'Cookies are small text files that a website stores in your browser. Local storage works in a similar way. We use them only for the purposes below.',
        ],
      },
      {
        title: 'What we use on veltica.com, the dashboard and the editor',
        blocks: [
          {
            table: {
              head: ['What', 'Purpose', 'How long', 'Type'],
              rows: [
                ['Sign-in session', 'Keeps you signed in and protects your Account', 'While you are signed in, renewed automatically', 'Strictly necessary'],
                ['Language preference', 'Remembers your language between the dashboard and the editor', 'Up to one year', 'Preference'],
                ['Appearance', 'Remembers your theme and accent color', 'Until you clear your browser data', 'Preference'],
                ['Editor preferences', 'Remembers interface choices such as pinned panels', 'Until you clear your browser data', 'Preference'],
              ],
            },
          },
          'We do not use advertising cookies, we do not use third-party analytics or tracking cookies on our own app, and we do not track you across other sites. If we add optional measurement in the future, we will tell you here and ask for your permission first.',
        ],
      },
      {
        title: 'What Sites hosted on véltica use',
        subs: [
          {
            title: 'Cookies set by véltica',
            blocks: [
              {
                table: {
                  head: ['What', 'Purpose', 'How long', 'Type'],
                  rows: [
                    ['Password access', 'Remembers that a visitor entered the right password on a password-protected Site or page', 'Up to 30 days', 'Strictly necessary'],
                    ['Owner mode', 'Shows the owner of a Site the owner’s bar while viewing their own published Site', 'While the owner is signed in', 'Strictly necessary'],
                  ],
                },
              },
              'Our visitor statistics do not use cookies. They count page views and, using a one-way hash of the IP address and browser that changes every day, unique visitors, without storing the raw IP address. We serve fonts from the Site’s own address and not from third-party font services.',
            ],
          },
          {
            title: 'Cookies set by what the Site owner adds',
            blocks: [
              'Site owners can add forms, bookings, a shop, custom code, embeds and integrations. Payments are made on a page provided by Stripe, which may set its own cookies under its policy. Analytics, advertising pixels, videos and other third-party embeds that a Site owner adds may set cookies of their own. The Site owner is responsible for those, and for showing any cookie notice and getting any consent the law requires on their Site. véltica does not control them.',
            ],
          },
        ],
      },
      {
        title: 'Managing cookies',
        blocks: [
          'You can delete or block cookies and clear local storage in your browser settings. If you block the strictly necessary ones, you will not be able to stay signed in or to enter a password-protected Site. Your browser’s “Do Not Track” and similar signals do not change what we do, because we do not track you across sites.',
        ],
      },
      {
        title: 'Changes and contact',
        blocks: ['We will update this policy if what we use changes, and the date at the top shows the version in force. Questions: {email}.'],
      },
    ],
  },

  es: {
    title: 'Política de cookies',
    summary: 'Las pocas cookies y el almacenamiento del navegador que usa véltica y para qué sirven.',
    intro: [
      'Esta Política de cookies explica las cookies y tecnologías similares, como el almacenamiento local del navegador, que véltica usa en veltica.com, en el panel y el editor, y en los Sitios que alojamos. Forma parte de nuestra [Política de privacidad](doc:privacy).',
    ],
    sections: [
      {
        title: 'Qué son',
        blocks: [
          'Las cookies son pequeños archivos de texto que un sitio web guarda en tu navegador. El almacenamiento local funciona de forma parecida. Las usamos solo para los fines de abajo.',
        ],
      },
      {
        title: 'Qué usamos en veltica.com, el panel y el editor',
        blocks: [
          {
            table: {
              head: ['Qué', 'Para qué', 'Cuánto dura', 'Tipo'],
              rows: [
                ['Sesión de acceso', 'Mantiene tu sesión iniciada y protege tu Cuenta', 'Mientras tengas la sesión iniciada, se renueva sola', 'Estrictamente necesaria'],
                ['Preferencia de idioma', 'Recuerda tu idioma entre el panel y el editor', 'Hasta un año', 'Preferencia'],
                ['Apariencia', 'Recuerda tu tema y color de acento', 'Hasta que borres los datos del navegador', 'Preferencia'],
                ['Preferencias del editor', 'Recuerda elecciones de la interfaz, como paneles fijados', 'Hasta que borres los datos del navegador', 'Preferencia'],
              ],
            },
          },
          'No usamos cookies publicitarias, no usamos cookies de analítica ni de seguimiento de terceros en nuestra propia aplicación y no te seguimos por otros sitios. Si en el futuro añadimos mediciones opcionales, te lo diremos aquí y te pediremos permiso antes.',
        ],
      },
      {
        title: 'Qué usan los Sitios alojados en véltica',
        subs: [
          {
            title: 'Cookies que pone véltica',
            blocks: [
              {
                table: {
                  head: ['Qué', 'Para qué', 'Cuánto dura', 'Tipo'],
                  rows: [
                    ['Acceso con contraseña', 'Recuerda que un visitante introdujo la contraseña correcta en un Sitio o página protegidos', 'Hasta 30 días', 'Estrictamente necesaria'],
                    ['Modo titular', 'Muestra al titular de un Sitio su barra mientras ve su propio Sitio publicado', 'Mientras el titular tenga la sesión iniciada', 'Estrictamente necesaria'],
                  ],
                },
              },
              'Nuestras estadísticas de visitantes no usan cookies. Cuentan las visitas y, con un hash unidireccional de la dirección IP y el navegador que cambia cada día, los visitantes únicos, sin guardar la IP en bruto. Servimos las fuentes desde la propia dirección del Sitio y no desde servicios de fuentes de terceros.',
            ],
          },
          {
            title: 'Cookies que ponen las cosas que añade el titular del Sitio',
            blocks: [
              'Los titulares de Sitios pueden añadir formularios, reservas, una tienda, código personalizado, contenido incrustado e integraciones. Los pagos se hacen en una página de Stripe, que puede poner sus propias cookies según su política. La analítica, los píxeles publicitarios, los vídeos y otros contenidos de terceros que añada un titular pueden poner cookies propias. El titular del Sitio es responsable de ellas, de mostrar cualquier aviso de cookies y de obtener el consentimiento que la ley exija en su Sitio. véltica no las controla.',
            ],
          },
        ],
      },
      {
        title: 'Gestionar las cookies',
        blocks: [
          'Puedes eliminar o bloquear cookies y borrar el almacenamiento local en la configuración de tu navegador. Si bloqueas las estrictamente necesarias, no podrás mantener la sesión iniciada ni entrar en un Sitio protegido con contraseña. Las señales de «No rastrear» de tu navegador y similares no cambian lo que hacemos, porque no te seguimos entre sitios.',
        ],
      },
      {
        title: 'Cambios y contacto',
        blocks: ['Actualizaremos esta política si cambia lo que usamos, y la fecha de arriba indica la versión en vigor. Preguntas: {email}.'],
      },
    ],
  },
}
